import { createReducer, on } from '@ngrx/store';
import { IAuthUser } from '../../models/LoginResponse';
import { AuthActions } from './auth.actions';

export interface AuthState {
  user: IAuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  captcha?: string | null;
  magicLinkSuccess?: boolean;
  isAccoutBlocked?: boolean;
  lockedTimer: number;
  isMFAEnabled?: boolean
  tokenIssuedAt?: number | null;
}

export const initialAuthState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  magicLinkSuccess: false,
  isAccoutBlocked: false,
  lockedTimer: 0,
  captcha: null,
  isMFAEnabled: false,
  tokenIssuedAt: null,
};

export const authReducer = createReducer(
  initialAuthState,
  on(AuthActions.login, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.loginSuccess, (state, { data }) => ({
    ...state,
    user: data.user,
    token: data.token,
    isAccoutBlocked: data.accountLocked,
    lockedTimer: 0,
    captcha: null,
    loading: false,
    error: null,
    isMFAEnabled: data.requiresOtp ?? false,
    tokenIssuedAt: Date.now(),
  })),
  on(AuthActions.loginFailure, (state, { error, data }) => ({
    ...state,
    loading: false,
    error,
    lockedTimer: data?.remainingLockTime ?? 0,
    isAccoutBlocked: data?.isBlocked,
  })),
  on(AuthActions.isSessionExist, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(AuthActions.isSessionExistSuccess, (state, { data }) => ({
    ...state,
    token: data.token,
    user: data.user,
    loading: false,
    error: null,
    tokenIssuedAt: Date.now(),
  })),
  on(AuthActions.isSessionExistFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AuthActions.logout, () => initialAuthState),
  on(AuthActions.logoutSuccess, (state) => ({
    ...state,
    token: null,
    user: null,
    loading: false,
    error: null,
  })),
  on(AuthActions.logoutFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AuthActions.restoreState, (state, { auth }) => ({
    ...state,
    ...auth,
  })),
  on(AuthActions.forgotPassword, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.resetPasswordLink, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.forgotPasswordSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.forgotPasswordFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.generateCaptcha, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.generateCaptchaSuccess, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    captcha: data.captcha,
  })),
  on(AuthActions.generateCaptchaFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
    captcha: null,
  })),
  on(AuthActions.useMagicLink, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.useMagicLinkSuccess, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    magicLinkSuccess: !data?.accountLocked,
    isAccoutBlocked: data?.accountLocked ?? false,
    lockedTimer: 0,
    captcha: null,
  })),
  on(AuthActions.useMagicLinkFailure, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    captcha: null,
    magicLinkSuccess: false,
    lockedTimer: data?.remainingLockTime ?? 0,
  })),
  on(AuthActions.validateMagicLink, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.validateMagicLinkSuccess, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    token: data.token,
    user: data.user,
    isAccoutBlocked: data.accountLocked,
    lockedTimer: 0,
    captcha: null,
    tokenIssuedAt: Date.now(),
  })),
  on(AuthActions.validateMagicLinkFailure, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    lockedTimer: data?.remainingLockTime ?? 0,
    isAccoutBlocked: data?.isBlocked,
  })),
  on(AuthActions.verifyResetToken, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.verifyResetTokenSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.verifyResetTokenFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.validateMagicOTP, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.validateMagicOTPSuccess, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    token: data.token,
    user: data.user,
    tokenIssuedAt: Date.now(),
  })),
  on(AuthActions.validateMagicOTPFailure, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    lockedTimer: data?.remainingLockTime ?? 0,
    isAccoutBlocked: data?.isBlocked,
  })),
  on(AuthActions.validateMFAOTP, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.validateMFAOTPSuccess, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    token: data.token,
    user: data.user,
    tokenIssuedAt: Date.now(),
  })),
  on(AuthActions.validateMFAOTPFailure, (state, { data }) => ({
    ...state,
    loading: false,
    error: null,
    lockedTimer: data?.remainingLockTime ?? 0,
    isAccoutBlocked: data?.isBlocked,
  })),
  on(AuthActions.resetPassword, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.resetPasswordSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.resetPasswordFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.resetPassword, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.resetPasswordSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.resetPasswordFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.changePassword, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AuthActions.changePasswordSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(AuthActions.changePasswordFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  }))
);
